import type { UserNode } from '@revelio/data-access';

import { PAY_FILTER_MAX } from '../../../filters/definitions/pay';
import { findFilterById } from '../../../filters/engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  isMultiFilter,
} from '../../../filters/types/filters';
import { isRangeFilter as isRangeFilterGuard } from '../filter-type-guards';
import { buildRangeNode, wrapExprNode } from './node-builders';
import { TalentDiscoveryV3Tags } from './types';

const MIN_YEAR = 1951;
const MAX_YEAR = 2100;
const DEFAULT_MIN_SALARY = 0;
const DEFAULT_MAX_SALARY = PAY_FILTER_MAX;

/**
 * Build graduation year filter node from filter state
 */
export const buildGraduationYearFilter = (
  filters: Filter[]
): UserNode | null => {
  const graduationYearFilter = findFilterById(
    filters,
    FilterId.GRADUATION_YEAR
  );

  if (!graduationYearFilter || graduationYearFilter.type !== FilterType.RANGE) {
    return null;
  }

  const { min, max, isUserSubmitted } = graduationYearFilter;

  // Only include if the filter has been user-submitted
  if (!isUserSubmitted) {
    return null;
  }

  // If no values are set, return null
  if (min === undefined && max === undefined) {
    return null;
  }

  const startYear = min ?? MIN_YEAR;
  const endYear = max ?? MAX_YEAR;

  // Graduation year doesn't support current/non-current (backend will ignore curr field)
  const exprNode = buildRangeNode(
    TalentDiscoveryV3Tags.GraduationYear,
    startYear,
    endYear,
    true
  );

  return wrapExprNode(exprNode, graduationYearFilter.isNot);
};

/**
 * Build pay filter node from filter state
 */
export const buildPayFilter = (filters: Filter[]): UserNode | null => {
  const payFilter = findFilterById(filters, FilterId.PAY);

  if (!payFilter || payFilter.type !== FilterType.RANGE) {
    return null;
  }

  const { min, max, isUserSubmitted, isCurrent = true } = payFilter;

  // Only include if the filter has been user-submitted
  if (!isUserSubmitted) {
    return null;
  }

  // If no values are set, return null
  if (min === undefined && max === undefined) {
    return null;
  }

  const startVal = min ?? DEFAULT_MIN_SALARY;
  const endVal = max ?? DEFAULT_MAX_SALARY;

  // Use SalaryBase for base salary (V2 uses salary_base)
  const exprNode = buildRangeNode(
    TalentDiscoveryV3Tags.SalaryBase,
    startVal,
    endVal,
    isCurrent
  );

  return wrapExprNode(exprNode, payFilter.isNot);
};

/**
 * Build years of experience filter node from filter state
 */
export const buildYearsOfExperienceFilter = (
  filters: Filter[]
): UserNode | null => {
  const yearsFilter = findFilterById(filters, FilterId.YEARS_OF_EXPERIENCE);

  if (!yearsFilter || yearsFilter.type !== FilterType.RANGE) {
    return null;
  }

  const { min, max, isUserSubmitted } = yearsFilter;

  // Only include if the filter has been user-submitted
  if (!isUserSubmitted) {
    return null;
  }

  // Treat both null and 0 as "not set" for range boundaries
  // This handles cases where AI returns 0 instead of null
  const hasMin = min !== undefined && min !== null && min !== 0;
  const hasMax = max !== undefined && max !== null && max !== 0;

  // If no values are set, return null
  if (!hasMin && !hasMax) {
    return null;
  }

  // Handle different range scenarios:
  // - Both min and max: use both values as-is
  // - Only min (greater than): use min + 1 to 100 (e.g., "greater than 20" → 21 to 100)
  // - Only max (less than): use 0 to max - 1 (e.g., "less than 20" → 0 to 19)
  const startYears = (() => {
    if (hasMin && hasMax) {
      return min;
    }
    if (hasMin) {
      // Greater than: add 1 to the minimum
      return min + 1;
    }
    return 0;
  })();

  const endYears = (() => {
    if (hasMin && hasMax) {
      return max;
    }
    if (hasMax) {
      // Less than: subtract 1 from the maximum
      return max - 1;
    }
    return 100;
  })();

  // Years of experience doesn't support current/non-current (backend will ignore curr field)
  const exprNode = buildRangeNode(
    TalentDiscoveryV3Tags.YearsOfExp,
    startYears,
    endYears,
    true
  );

  return wrapExprNode(exprNode, yearsFilter.isNot);
};

/**
 * Build multi pay filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiPayFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiPay = findFilterById(filters, FilterId.MULTI_PAY);

  if (!multiPay || !isMultiFilter(multiPay)) {
    return null;
  }

  if (multiPay.instances.length === 0) {
    return null;
  }

  const instanceNodes: UserNode[] = [];
  for (const instance of multiPay.instances) {
    if (!isRangeFilterGuard(instance)) continue;

    const { min, max, isUserSubmitted, isCurrent = true, isNot } = instance;

    // Only include if the filter has been user-submitted
    if (!isUserSubmitted) {
      continue;
    }

    // If no values are set, skip
    if (min === undefined && max === undefined) {
      continue;
    }

    const startVal = min ?? DEFAULT_MIN_SALARY;
    const endVal = max ?? DEFAULT_MAX_SALARY;

    // Use SalaryBase for base salary (V2 uses salary_base)
    const exprNode = buildRangeNode(
      TalentDiscoveryV3Tags.SalaryBase,
      startVal,
      endVal,
      isCurrent
    );

    instanceNodes.push(wrapExprNode(exprNode, isNot));
  }

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};

/**
 * Build multi graduation year filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiGraduationYearFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiGraduationYear = findFilterById(
    filters,
    FilterId.MULTI_GRADUATION_YEAR
  );

  if (!multiGraduationYear || !isMultiFilter(multiGraduationYear)) {
    return null;
  }

  if (multiGraduationYear.instances.length === 0) {
    return null;
  }

  const instanceNodes: UserNode[] = [];
  for (const instance of multiGraduationYear.instances) {
    if (!isRangeFilterGuard(instance)) continue;

    const { min, max, isUserSubmitted, isNot } = instance;

    // Only include if the filter has been user-submitted
    if (!isUserSubmitted) {
      continue;
    }

    // If no values are set, skip
    if (min === undefined && max === undefined) {
      continue;
    }

    const startYear = min ?? MIN_YEAR;
    const endYear = max ?? MAX_YEAR;

    // Graduation year doesn't support current/non-current (backend will ignore curr field)
    const exprNode = buildRangeNode(
      TalentDiscoveryV3Tags.GraduationYear,
      startYear,
      endYear,
      true
    );

    instanceNodes.push(wrapExprNode(exprNode, isNot));
  }

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};

/**
 * Build multi years of experience filter node from filter state.
 * Each instance becomes a node, and instances are ANDed together.
 * Returns an array of nodes when there are multiple instances (to avoid nested AND nodes).
 */
export const buildMultiYearsOfExperienceFilter = (
  filters: Filter[]
): UserNode | UserNode[] | null => {
  const multiYearsOfExperience = findFilterById(
    filters,
    FilterId.MULTI_YEARS_OF_EXPERIENCE
  );

  if (!multiYearsOfExperience || !isMultiFilter(multiYearsOfExperience)) {
    return null;
  }

  if (multiYearsOfExperience.instances.length === 0) {
    return null;
  }

  const instanceNodes: UserNode[] = [];
  for (const instance of multiYearsOfExperience.instances) {
    if (!isRangeFilterGuard(instance)) continue;

    const { min, max, isUserSubmitted, isNot } = instance;

    // Only include if the filter has been user-submitted
    if (!isUserSubmitted) {
      continue;
    }

    // Treat both null and 0 as "not set" for range boundaries
    const hasMin = min !== undefined && min !== null && min !== 0;
    const hasMax = max !== undefined && max !== null && max !== 0;

    // If no values are set, skip
    if (!hasMin && !hasMax) {
      continue;
    }

    // Handle different range scenarios
    const startYears = (() => {
      if (hasMin && hasMax) {
        return min;
      }
      if (hasMin) {
        return min + 1;
      }
      return 0;
    })();

    const endYears = (() => {
      if (hasMin && hasMax) {
        return max;
      }
      if (hasMax) {
        return max - 1;
      }
      return 100;
    })();

    // Years of experience doesn't support current/non-current (backend will ignore curr field)
    const exprNode = buildRangeNode(
      TalentDiscoveryV3Tags.YearsOfExp,
      startYears,
      endYears,
      true
    );

    instanceNodes.push(wrapExprNode(exprNode, isNot));
  }

  if (instanceNodes.length === 0) return null;
  if (instanceNodes.length === 1) {
    const [singleNode] = instanceNodes;
    return singleNode ?? null;
  }

  return instanceNodes;
};
